/**
 * Code step of the Connect Splyt page. Confirms the SMS code that the phone
 * step sent; the pending confirmation lives in firebaseAuth.js module state,
 * so a refresh here goes back to the phone step (same guard as the join flow).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  verifyCode,
  resendCode,
  createRecaptchaVerifier,
  getPendingPhone,
  getPendingDisplayPhone,
  hasPendingVerification,
  clearPending,
} from '../join/firebaseAuth';
import { track, identify, EVENTS, SCREEN } from '../../analytics/track';
import useConnectStyles from './connectStyles';
import ConnectShell, { useIsDesktop } from './ConnectShell';
import { formatCountdown } from './connectHelpers';

const RECAPTCHA_CONTAINER_ID = 'connect-verify-recaptcha-container';
const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

export default function ConnectCodeStep({ onVerified, onWrongNumber }) {
  const classes = useConnectStyles();
  const isDesktop = useIsDesktop();
  const inputRef = useRef(null);

  const phone = getPendingPhone();
  const displayPhone = getPendingDisplayPhone();

  const [code, setCode] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(RESEND_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  useEffect(function trackVerifyScreen() {
    track(EVENTS.SCREEN_VIEWED, { screen_name: SCREEN.VERIFY });
  }, []);

  // No pending confirmation (a refresh, or a direct landing): start over.
  useEffect(() => {
    if (!hasPendingVerification()) onWrongNumber();
  }, [onWrongNumber]);

  useEffect(() => {
    const verifier = createRecaptchaVerifier(RECAPTCHA_CONTAINER_ID);
    setRecaptchaVerifier(verifier);
    return () => {
      try {
        verifier.clear();
      } catch (err) {
        // Already cleared by Firebase; nothing to release.
      }
    };
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return undefined;
    }
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const submitVerification = useCallback(
    async (codeToVerify) => {
      if (isLoading || !hasPendingVerification()) return;
      setIsLoading(true);
      setError('');
      try {
        const { user } = await verifyCode(codeToVerify);
        identify(user);
        clearPending();
        onVerified(user);
      } catch (err) {
        console.error('[connect] verification failed:', err);
        setIsShaking(true);
        setError(err.message || 'Verification failed. Please try again.');
        setTimeout(() => {
          setIsShaking(false);
          setCode('');
          if (inputRef.current) inputRef.current.focus();
        }, 200);
        setIsLoading(false);
      }
    },
    [isLoading, onVerified],
  );

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(value);
    if (error) setError('');
    if (value.length === CODE_LENGTH) submitVerification(value);
  };

  const handleResend = async () => {
    if (!canResend || !hasPendingVerification() || isLoading) return;
    setCanResend(false);
    setTimeLeft(RESEND_SECONDS);
    setError('');
    setCode('');
    setIsLoading(true);
    try {
      await resendCode(recaptchaVerifier);
    } catch (err) {
      console.error('[connect] resend failed:', err);
      setError(err.message || 'Failed to resend code. Please try again.');
      setCanResend(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWrongNumber = () => {
    clearPending();
    onWrongNumber();
  };

  const handleVerifyClick = () => {
    if (code.length === CODE_LENGTH && !isLoading) submitVerification(code);
  };

  const focusInput = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  const isCodeComplete = code.length === CODE_LENGTH;
  const digits = code.split('').concat(Array(CODE_LENGTH - code.length).fill(''));
  const activeIndex = Math.min(code.length, CODE_LENGTH - 1);

  const codeBoxes = (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={`${classes.codeBoxesContainer} ${isShaking ? classes.codeBoxesShake : ''}`}
      onClick={focusInput}
      onKeyDown={focusInput}
      role="group"
      aria-label="Verification code input"
    >
      {digits.map((digit, index) => {
        const isFilled = digit !== '';
        const isActive = isFocused && index === activeIndex && !isLoading;
        let boxClass = classes.codeBox;
        if (isActive) boxClass += ` ${classes.codeBoxActive}`;
        else if (isFilled) boxClass += ` ${classes.codeBoxFilled}`;
        else boxClass += ` ${classes.codeBoxEmpty}`;
        return (
          <div key={index} className={boxClass}>
            {digit || (isActive ? (
              <div className={classes.cursor} />
            ) : (
              <div className={classes.emptyPlaceholder} />
            ))}
          </div>
        );
      })}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        className={classes.hiddenInput}
        value={code}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={isLoading}
        aria-label="6-digit verification code"
      />
    </div>
  );

  const content = (
    <>
      <h2 className={`${classes.title} ${isDesktop ? classes.titleDesktop : classes.titleMobile}`}>
        Check your texts
      </h2>
      <p className={classes.subtitle}>
        We sent a 6-digit code to{' '}
        <span className={classes.phoneNumber}>{displayPhone || phone || 'your number'}</span>.
      </p>

      {codeBoxes}

      {error && <div className={classes.errorMessage} role="alert">{error}</div>}

      <div className={classes.metaRow}>
        {canResend ? (
          <button type="button" className={classes.inlineLink} onClick={handleResend} disabled={isLoading}>
            Send a new code
          </button>
        ) : (
          <span className={classes.timerText}>
            Resend code in{' '}
            <span className={classes.timerCount}>{formatCountdown(timeLeft)}</span>
          </span>
        )}
        <button type="button" className={classes.inlineLink} onClick={handleWrongNumber} disabled={isLoading}>
          Wrong number?
        </button>
      </div>
    </>
  );

  const footer = (
    <button
      type="button"
      className={classes.primaryButton}
      disabled={!isCodeComplete || isLoading}
      onClick={handleVerifyClick}
      // iOS fires blur before click: without this the first tap on the pinned
      // bar only closes the keyboard and the button slides out from under the finger.
      onMouseDown={(e) => e.preventDefault()}
    >
      {isLoading ? 'Verifying...' : 'Verify'}
    </button>
  );

  return (
    <>
      <ConnectShell footer={footer}>{content}</ConnectShell>
      <span id={RECAPTCHA_CONTAINER_ID} />
    </>
  );
}

ConnectCodeStep.propTypes = {
  onVerified: PropTypes.func.isRequired,
  onWrongNumber: PropTypes.func.isRequired,
};
